import {
  Box,
  Typography,
  Stack,
  Button,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Sheet,
} from '@mui/joy';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { ContentCopy, OpenInNew } from '@mui/icons-material';
import useUser from './auth/useUser';
import LargeLoader from './LargeLoader';
import { useFeedback } from './feedback/Service';
import { parseGraphQLError } from '../utils';
import { DEFAULT_QUESTIONNAIRE } from '../constants';
import FormConstructor, { hasInvalidScreeningQuestions } from './FormConstructor';
import InterviewQuestionsEditor, { hasInvalidInterviewQuestions } from './InterviewQuestionsEditor';
import useUploadPdf from './useUploadPdf';
import useUnsavedChangesGuard from './useUnsavedChangesGuard';
import useTracking from './useTracking';
import POSITIONS_QUERY from '../gql/admin/positions.query';
import { parseQuestions } from './DefaultInterviewQuestionsSelector';
import { CORE_JOB_FIELDS } from '../gql/fragments/job.fragment';
// import { ATSJobLinker } from './integrations';
import JobDescriptionInput, { getDescriptionValidationError, isParsePending } from './JobDescriptionInput';
import useJobParsePolling from './useJobParsePolling';

const baseSteps = [
  { title: 'Role', label: 'What role are you hiring for?' },
  { title: 'Description', label: 'Add your job description' },
  { title: 'Interview', label: 'Customize your interview' },
];

const screeningStep = { title: 'Screening', label: 'Add screening questions' };

const POST_JOB = gql`
  ${CORE_JOB_FIELDS}
  mutation PostJob($job: JobInput!) {
    postJob(job: $job) {
      ...CoreJobFields
    }
  }
`;

const DELETE_JOB = gql`
  ${CORE_JOB_FIELDS}
  mutation deleteJob($_id: String!) {
    deleteJob(_id: $_id) {
      _id
      jobs {
        ...CoreJobFields
      }
    }
  }
`;

const initialState = {
  title: '',
  pdfUrl: '',
  jobDescription: '',
  questions: DEFAULT_QUESTIONNAIRE,
  interviewQuestions: [],
  published: true,
  comment: '',
  hideClientName: false,
  userEditedDescription: false,
};

const reducer = (state, newState) => ({
  ...state,
  ...newState,
});

const transformPosition = (position) => {
  const questions = (position.defaultQuestions || []).map(dq => ({
    sectionId: dq.sectionId,
    sectionTitle: dq.section?.name,
    maxQuestions: dq.section?.maxQuestions || (dq.questions?.length || 0),
    questionType: dq.section?.questionType,
    questions: parseQuestions(dq),
  }));

  return {
    id: position._id,
    title: position.title,
    questions,
  };
};

const buildTemplates = (positions) => (positions || []).map(p => transformPosition(p));

const parseJsonArray = (value, fallback = []) => {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error('Error parsing job JSON field', error);
    return fallback;
  }
};

const parseInterviewQuestions = (interviewQuestionsString, defaultInterviewQuestions = []) => {
  if (!interviewQuestionsString) return defaultInterviewQuestions;
  try {
    const parsed = JSON.parse(interviewQuestionsString);
    return parsed.map(section => {
      const defaultSection = defaultInterviewQuestions.find(x => x.sectionId === section.sectionId);
      return {
        ...defaultSection,
        ...section,
        questions: section.questions || [],
      };
    });
  } catch (error) {
    console.error('Error parsing interview questions', error);
    return defaultInterviewQuestions;
  }
};

const getStateFromInitialData = (initialData, defaultInterviewQuestions = []) => {
  if (!initialData) return { ...initialState };

  return {
    ...initialData,
    comment: initialData?.comment || '',
    jobDescription: initialData?.jobDescription || '',
    hideClientName: initialData?.hideClientName || false,
    questions: parseJsonArray(initialData.questions, []),
    interviewQuestions: parseInterviewQuestions(initialData?.interviewQuestions, defaultInterviewQuestions),
    userEditedDescription: false,
    applications: undefined,
    allApplications: undefined,
    hasJobDescription: undefined,
    candidatesStats: undefined,
    createdAt: undefined,
    updatedAt: undefined,
    __typename: undefined,
  };
};

const prepareJobInput = (state, { forceEmptyQuestions = false } = {}) => {
  const data = { ...state };
  data.atsTargetStageId = undefined;
  data.atsSourceStageId = undefined;
  data.ashbyFlowMode = undefined;
  data.greenhouseFlowMode = undefined;
  data.applications = undefined;
  data.allApplications = undefined;
  data.hasJobDescription = undefined;
  data.jobDescriptionStatus = undefined;
  data.jobDescriptionError = undefined;
  data.userEditedDescription = undefined;
  data.candidatesStats = undefined;
  data.createdAt = undefined;
  data.updatedAt = undefined;
  data.__typename = undefined;
  const trimmedDescription = (data.jobDescription || '').trim();
  data.jobDescription = trimmedDescription || undefined;
  data.questions = JSON.stringify(forceEmptyQuestions ? [] : (data.questions || []));
  data.interviewQuestions = JSON.stringify(
    [...(data.interviewQuestions || [])].map(({ sectionId, questions, prefix }) => ({
      sectionId,
      questions,
      prefix,
    }))
  );
  return data;
};

const getFinishRoute = (next, jobId) => {
  if ((next === 'import-candidates' || next === 'invite-candidates') && jobId) {
    return `/onboarding/invite-candidates/${jobId}`;
  }
  return '/jobs';
};

const OnboardingJobEditor = () => {
  const [state, setStateInternal] = useReducer(reducer, initialState);
  const [activeStep, setActiveStep] = useState(0);
  const [showAdditionalSettings, setShowAdditionalSettings] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const pageRef = useRef(null);
  const { jobId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const feedback = useFeedback();
  const { user, loading, refetch } = useUser({ fetchPolicy: 'cache-and-network', refetchOnWindowVisible: true });
  const [uploadObject, uploadPdf, resetUpload] = useUploadPdf();
  const { data: positionsData, loading: positionsLoading } = useQuery(POSITIONS_QUERY, { variables: { limit: 200 } });
  const { trackCopyJobUrl, trackOpenDemo } = useTracking();
  const loadedJobIdRef = useRef(null);
  const originalPdfUrlRef = useRef(null);

  const isExistingJob = jobId && jobId !== 'new';
  const nextRoute = useMemo(() => new URLSearchParams(location.search).get('next'), [location.search]);
  const isAtsMode = nextRoute === 'import-candidates' || (user?.client?.usesATS === true && !!user?.client?.selectedATSProvider);
  const steps = useMemo(() => isAtsMode ? baseSteps : [...baseSteps, screeningStep], [isAtsMode]);
  const initialData = useMemo(
    () => (user?.client?.jobs || []).find(x => x._id === jobId),
    [user, jobId]
  );
  const templates = useMemo(() => buildTemplates(positionsData?.positions || []), [positionsData]);
  const defaultInterviewQuestions = useMemo(() => templates[0]?.questions || [], [templates]);

  const setState = (newState) => {
    setStateInternal(newState);
    setHasUnsavedChanges(true);
  };

  useUnsavedChangesGuard(hasUnsavedChanges);

  const [submit, { loading: posting }] = useMutation(POST_JOB, {
    update(cache, { data: { postJob } }) {
      if (!user?.client?._id || !postJob) return;
      cache.modify({
        id: cache.identify({ _id: user.client._id, __typename: 'Client' }),
        fields: {
          jobs: () => postJob,
        },
      });
    },
  });

  const [remove, { loading: deleting }] = useMutation(DELETE_JOB, {
    update(cache, { data: { deleteJob: client } }) {
      if (!client?._id) return;
      cache.modify({
        id: cache.identify({ _id: client._id, __typename: 'Client' }),
        fields: {
          jobs() {
            return client.jobs;
          },
        },
      });
    },
  });

  useEffect(() => {
    if (!initialData || !templates.length) return;
    if (loadedJobIdRef.current === initialData._id) return;
    setStateInternal(getStateFromInitialData(initialData, defaultInterviewQuestions));
    setHasUnsavedChanges(false);
    loadedJobIdRef.current = initialData._id;
    originalPdfUrlRef.current = initialData.pdfUrl || null;
  }, [initialData, templates, defaultInterviewQuestions]);

  // Live sync of server-driven fields (parse status/text). Updates without marking
  // hasUnsavedChanges and preserves the user's in-flight textarea edits.
  const remoteJobId = initialData?._id;
  const remoteDescription = initialData?.jobDescription;
  const remoteStatus = initialData?.jobDescriptionStatus;
  const remoteError = initialData?.jobDescriptionError;
  const remoteHasJobDescription = initialData?.hasJobDescription;
  useEffect(() => {
    if (!remoteJobId) return;
    if (loadedJobIdRef.current !== remoteJobId) return;
    const updates = {
      jobDescriptionStatus: remoteStatus,
      jobDescriptionError: remoteError,
      hasJobDescription: remoteHasJobDescription,
    };
    if (!state.userEditedDescription && remoteDescription) {
      updates.jobDescription = remoteDescription;
    }
    setStateInternal(updates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteJobId, remoteDescription, remoteStatus, remoteError, remoteHasJobDescription]);

  useJobParsePolling({
    status: state.jobDescriptionStatus,
    refetch,
    jobId: isExistingJob ? jobId : loadedJobIdRef.current,
  });

  // Surface a snackbar the first time we observe the status flip to 'failed' —
  // the inline Alert in JobDescriptionInput may be off-screen on a later step. (C2)
  // Only fire when the failure is for the user's current saved PDF; if they've
  // since uploaded a replacement (cancel mode) or removed the PDF, the failure
  // relates to a file they've already moved past and the snackbar would mislead.
  const prevParseStatusRef = useRef(null);
  useEffect(() => {
    const status = state.jobDescriptionStatus;
    const pdfMatchesBaseline = !!state.pdfUrl && state.pdfUrl === originalPdfUrlRef.current;
    if (
      prevParseStatusRef.current &&
      prevParseStatusRef.current !== 'failed' &&
      status === 'failed' &&
      pdfMatchesBaseline
    ) {
      feedback.snackbar({
        text: `PDF parsing failed${state.jobDescriptionError ? `: ${state.jobDescriptionError}` : ''}`,
        type: 'error',
      });
    }
    prevParseStatusRef.current = status;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.jobDescriptionStatus, state.jobDescriptionError]);

  useEffect(() => {
    if (!isExistingJob && templates.length && state.interviewQuestions.length === 0) {
      setStateInternal(prev => ({ ...prev, interviewQuestions: defaultInterviewQuestions }));
      setHasUnsavedChanges(false);
    }
  }, [isExistingJob, templates, defaultInterviewQuestions, state.interviewQuestions.length]);

  useEffect(() => {
    if (activeStep > steps.length - 1) {
      setActiveStep(steps.length - 1);
    }
  }, [activeStep, steps.length]);

  const interviewQuestionCount = useMemo(
    () => state.interviewQuestions.map(x => x.questions?.length || 0).reduce((a, b) => a + b, 0),
    [state.interviewQuestions]
  );

  const handleChange = ({ target: { name, value, checked, type } }) => {
    setState({ [name]: type === 'checkbox' ? checked : value });
  };

  const scrollToTop = () => {
    requestAnimationFrame(() => {
      const scrollContainer = pageRef.current?.closest('.MainContent');
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  };

  const handlePdfChange = async (file) => {
    const pdfUpload = await uploadPdf(file);
    if (pdfUpload) {
      // User accepted the overwrite confirm in JobDescriptionInput (if any),
      // so the parser is allowed to fill the description again.
      setState({ pdfUrl: pdfUpload, userEditedDescription: false });
    }
  };

  const handlePdfRemove = () => {
    const baseline = originalPdfUrlRef.current || '';
    if (state.pdfUrl && state.pdfUrl !== baseline) {
      setState({ pdfUrl: baseline });
    } else {
      setState({ pdfUrl: '' });
    }
    // Clear the FileUpload card (filename + progress). The hook's abort handler
    // only fires for in-flight uploads; a completed upload's card sticks around
    // unless we reset it explicitly.
    resetUpload();
  };

  const handleDescriptionTextChange = (value) => {
    setState({ jobDescription: value, userEditedDescription: true });
  };

  const handleDeleteClick = async e => {
    e.stopPropagation();
    const confirm = await feedback.confirm({ title: 'Remove posted job?' });
    if (!confirm) return;

    try {
      setHasUnsavedChanges(false);
      await remove({ variables: { _id: jobId } });
      feedback.snackbar({ text: 'Job removed!', type: 'success' });
      navigate('/jobs', { replace: true });
    } catch (error) {
      console.error('Delete request error', error);
      feedback.snackbar({ text: parseGraphQLError(error), type: 'error' });
    }
  };

  const validateStep = (step) => {
    if (step === 0 && !state.title.trim()) {
      feedback.snackbar({ text: 'Please enter a job title', type: 'error' });
      return false;
    }

    if (step === 1) {
      const pdfChanged = (state.pdfUrl || '') !== (originalPdfUrlRef.current || '');
      const descErr = getDescriptionValidationError({ ...state, pdfChanged });
      if (descErr) {
        feedback.snackbar({ text: descErr, type: 'error' });
        return false;
      }
    }

    if (step === 2) {
      if (positionsLoading && state.interviewQuestions.length === 0) {
        feedback.snackbar({ text: 'Interview questions are still loading. Please try again in a moment.', type: 'info' });
        return false;
      }
      if (hasInvalidInterviewQuestions(state.interviewQuestions)) {
        feedback.snackbar({ text: 'All interview question fields are required. Please fill in all fields.', type: 'error' });
        return false;
      }
    }

    if (!isAtsMode && step === 3 && hasInvalidScreeningQuestions(state.questions)) {
      feedback.snackbar({ text: 'Please fill in all required fields for each screening question.', type: 'error' });
      return false;
    }

    return true;
  };

  const handleNext = async () => {
    if (!validateStep(activeStep)) return;

    const isFinalStep = activeStep >= steps.length - 1;

    // At final save, re-check every step. The wizard normally validates only the
    // current step; if the user backtracked and emptied a required field, the
    // earlier step's check never re-fires until we do this. (B1/C1)
    if (isFinalStep) {
      for (let i = 0; i < steps.length; i += 1) {
        if (i === activeStep) continue;
        if (!validateStep(i)) {
          setActiveStep(i);
          scrollToTop();
          return;
        }
      }
    }

    if (!isFinalStep) {
      // Partial save on leaving the Description step when the PDF changed —
      // this is what triggers backend parsing. We await the save (quick) but
      // not the parsing itself; polling keeps status fresh in the background.
      const pdfChanged = (state.pdfUrl || null) !== (originalPdfUrlRef.current || null);
      if (activeStep === 1 && pdfChanged) {
        try {
          const { data: { postJob } } = await submit({ variables: { job: prepareJobInput(state, { forceEmptyQuestions: isAtsMode }) } });
          const savedJobs = Array.isArray(postJob) ? postJob : [postJob].filter(Boolean);
          const savedJob = isExistingJob
            ? savedJobs.find(job => job._id === jobId) || initialData
            : savedJobs[savedJobs.length - 1];
          if (savedJob?._id) {
            originalPdfUrlRef.current = savedJob.pdfUrl || null;
            loadedJobIdRef.current = savedJob._id;
            // State and backend agree now; let future server-side parse results
            // sync through the live-sync effect again.
            setStateInternal({ userEditedDescription: false });
            setHasUnsavedChanges(false);
            if (!isExistingJob) {
              navigate(`/onboarding/job/${savedJob._id}${location.search}`, { replace: true });
            }
          }
        } catch (error) {
          console.error('OnboardingJobEditor partial save error', error);
          feedback.snackbar({ text: parseGraphQLError(error), type: 'error' });
          return;
        }
      }
      setActiveStep(step => step + 1);
      scrollToTop();
      return;
    }

    try {
      const { data: { postJob } } = await submit({ variables: { job: prepareJobInput(state, { forceEmptyQuestions: isAtsMode }) } });
      const savedJobs = Array.isArray(postJob) ? postJob : [postJob].filter(Boolean);
      const savedJob = isExistingJob
        ? savedJobs.find(job => job._id === jobId) || initialData
        : savedJobs[savedJobs.length - 1];
      const savedJobId = savedJob?._id || jobId;

      if (state.published) {
        window.gtag?.('event', 'job_posted', {});
      }

      setHasUnsavedChanges(false);
      setStateInternal({ userEditedDescription: false });
      // Read status from the saved job, not local closure — the just-saved value is
      // fresher than what's in component state at this point.
      const parsePending = isParsePending(savedJob?.jobDescriptionStatus || state.jobDescriptionStatus);
      const successText = isExistingJob
        ? (parsePending ? "Job saved — we'll keep extracting text in the background." : 'Job saved!')
        : (parsePending ? "Job created — we'll keep extracting text in the background." : 'Job created!');
      feedback.snackbar({ text: successText, type: 'success' });
      navigate(getFinishRoute(nextRoute, savedJobId), { replace: true });
    } catch (error) {
      console.error('OnboardingJobEditor error', error);
      feedback.snackbar({ text: parseGraphQLError(error), type: 'error' });
    }
  };

  const handleStepClick = (index) => {
    if (index <= activeStep) {
      setActiveStep(index);
    }
  };

  const renderProgress = () => (
    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 3 }}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%' }}>
          {steps.map((step, index) => {
            const isActive = activeStep === index;
            const isComplete = activeStep > index;

            return (
              <Sheet
                key={step.title}
                component="button"
                type="button"
                variant={isActive ? 'soft' : 'plain'}
                color={isActive || isComplete ? 'primary' : 'neutral'}
                disabled={index > activeStep}
                onClick={() => handleStepClick(index)}
                sx={{
                  flex: 1,
                  border: 0,
                  borderRadius: 'sm',
                  p: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  textAlign: 'left',
                  cursor: index <= activeStep ? 'pointer' : 'default',
                  opacity: index > activeStep ? 0.72 : 1,
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    flex: '0 0 auto',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isActive || isComplete ? 'primary.solidBg' : 'neutral.softBg',
                    color: isActive || isComplete ? 'primary.solidColor' : 'text.tertiary',
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {isComplete ? <CheckRoundedIcon sx={{ fontSize: 18 }} /> : index + 1}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography level="body-xs" color="neutral">Step {index + 1}</Typography>
                  <Typography level="title-sm" sx={{ overflowWrap: 'anywhere' }}>{step.title}</Typography>
                </Box>
              </Sheet>
            );
          })}
        </Stack>
        <LinearProgress determinate value={((activeStep + 1) / steps.length) * 100} size="sm" />
      </Stack>
    </Sheet>
  );

  const renderStepIntro = (title, subtitle) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography level="h3" sx={{ fontWeight: 700, mb: 0.75 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography level="body-md" color="neutral" sx={{ maxWidth: 760 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  const renderPublishingSettings = () => (
    <Card variant="outlined">
      <CardContent>
        <Typography level="title-md" sx={{ mb: 2 }}>Publishing Settings</Typography>
        <Stack spacing={1.5}>
          <Sheet variant="outlined" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, p: 2, borderRadius: 'sm' }}>
            <Box>
              <Typography level="title-sm">Public Job URL</Typography>
              <Typography level="body-sm" color="neutral">
                {state.published ? 'Job is publicly accessible' : 'Job is in draft mode'}
              </Typography>
            </Box>
            <Switch
              checked={!!state.published}
              onChange={handleChange}
              name="published"
              color={state.published ? 'success' : 'neutral'}
              slotProps={{ input: { name: 'published' } }}
            />
          </Sheet>

          <Sheet variant="outlined" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, p: 2, borderRadius: 'sm' }}>
            <Box>
              <Typography level="title-sm">Hide Company Name</Typography>
              <Typography level="body-sm" color="neutral">
                The company name will be {state.hideClientName ? 'hidden' : 'visible'} for candidates applying to this job.
              </Typography>
            </Box>
            <Switch
              checked={!!state.hideClientName}
              onChange={handleChange}
              name="hideClientName"
              color={state.hideClientName ? 'success' : 'neutral'}
              slotProps={{ input: { name: 'hideClientName' } }}
            />
          </Sheet>

          {isExistingJob && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              {initialData?.published && state.published && (
                <Button
                  variant="soft"
                  endDecorator={<ContentCopy />}
                  sx={{ flex: 1 }}
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/company/${user.client._id}/job/${jobId}/intro`);
                    trackCopyJobUrl(user.client._id, jobId, state.title);
                    feedback.snackbar({ text: 'Job URL copied to clipboard!', type: 'success' });
                  }}
                >
                  Copy Job URL
                </Button>
              )}
              <Button
                variant="soft"
                component="a"
                href={`${window.location.origin}/company/${user.client._id}/job/${jobId}/intro`}
                target="_blank"
                rel="noopener noreferrer"
                endDecorator={<OpenInNew />}
                sx={{ flex: 1 }}
                onClick={() => trackOpenDemo(user.client._id, jobId)}
              >
                Open Demo
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  const renderStepOne = () => (
    <Stack spacing={2.5}>
      {renderStepIntro('What role are you hiring for?')}

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <FormControl required>
              <FormLabel>Job Title</FormLabel>
              <Input
                placeholder="e.g. Sales Development Representative"
                value={state.title}
                onChange={handleChange}
                name="title"
                size="lg"
              />
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      <Box>
        <Button
          variant="outlined"
          color="neutral"
          endDecorator={
            <ExpandMoreIcon
              sx={{
                transform: showAdditionalSettings ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.16s ease',
              }}
            />
          }
          onClick={() => setShowAdditionalSettings(open => !open)}
        >
          Additional Settings
        </Button>

        {showAdditionalSettings && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <FormControl>
              <FormLabel>Comment (internal use only)</FormLabel>
              <Input
                placeholder="Internal notes, owner, source, or launch context"
                value={state.comment}
                onChange={handleChange}
                name="comment"
                size="lg"
              />
            </FormControl>
            {renderPublishingSettings()}
            {isExistingJob && (
              <>
                {/* {isAtsMode && (
                  <>
                    <ATSJobLinker job={state} providerId="ashby" />
                    <ATSJobLinker job={state} providerId="greenhouse" />
                  </>
                )} */}
                <Card variant="outlined" color="danger">
                  <CardContent>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                      <Box>
                        <Typography level="title-sm">Delete Job</Typography>
                        <Typography level="body-sm" color="neutral">
                          Permanently remove this job and return to the jobs list.
                        </Typography>
                      </Box>
                      <Button
                        variant="plain"
                        color="danger"
                        loading={deleting}
                        onClick={handleDeleteClick}
                        startDecorator={<DeleteOutlineRoundedIcon />}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </>
            )}
          </Stack>
        )}
      </Box>
    </Stack>
  );

  const renderStepTwo = () => (
    <Stack spacing={2.5}>
      {renderStepIntro(
        'Add your job description',
        "We'll use it to evaluate how well candidates match this role. The more detailed it is, the more accurate the evaluation will be."
      )}
      <JobDescriptionInput
        pdfUrl={state.pdfUrl}
        originalPdfUrl={originalPdfUrlRef.current}
        jobDescription={state.jobDescription}
        jobDescriptionStatus={state.jobDescriptionStatus}
        jobDescriptionError={state.jobDescriptionError}
        uploadObject={uploadObject}
        onPdfChange={handlePdfChange}
        onPdfRemove={handlePdfRemove}
        onTextChange={handleDescriptionTextChange}
        disabled={posting}
        userEdited={state.userEditedDescription}
      />
    </Stack>
  );

  const renderStepThree = () => (
    <Stack spacing={2.5}>
      {renderStepIntro('Customize your interview')}
      <Sheet variant="soft" color="primary" sx={{ p: 2, borderRadius: 'md' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography level="title-sm">Interview structure</Typography>
            <Typography level="body-sm" color="neutral">
              Review the default template and tailor questions for this role.
            </Typography>
          </Box>
          <Chip variant="solid" color="primary">{interviewQuestionCount} Questions</Chip>
        </Stack>
      </Sheet>
      <InterviewQuestionsEditor
        questions={state.interviewQuestions}
        setQuestions={questions => setState({ interviewQuestions: questions })}
        embedded
      />
    </Stack>
  );

  const renderStepFour = () => (
    <Stack spacing={2.5}>
      {renderStepIntro('Add screening questions (optional)', 'Filter out unqualified candidates before the interview')}
      <Sheet variant="soft" color="primary" sx={{ p: 2, borderRadius: 'md' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography level="title-sm">Candidate pre-screen</Typography>
            <Typography level="body-sm" color="neutral">
              Keep the defaults, customize them for this role, or remove every question to send candidates straight to the interview.
            </Typography>
          </Box>
          <Chip variant="solid" color="primary">{state.questions.length} Questions</Chip>
        </Stack>
      </Sheet>
      <FormConstructor
        questions={state.questions}
        setQuestions={questions => setState({ questions })}
        embedded
      />
    </Stack>
  );

  if (loading && (!user || isExistingJob)) {
    return <LargeLoader />;
  }

  const isNotFound = isExistingJob && !initialData && user && !loading && !posting;
  if (isNotFound) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography level="h2" color="danger">Job not found</Typography>
        <Button onClick={() => navigate('/jobs')} sx={{ mt: 2 }}>
          Back to Jobs
        </Button>
      </Box>
    );
  }

  return (
    <Box ref={pageRef} sx={{ maxWidth: 980, mx: 'auto', p: { xs: 2, md: 3 }, pb: 5 }}>
      <Box sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Box>
            <Typography level="h2" sx={{ mb: 0.75, fontWeight: 700 }}>
              {isExistingJob ? 'Review Job Setup' : 'Create Job'}
            </Typography>
            <Typography level="body-md" color="neutral">
              Set up the role, description, and interview before inviting candidates.
            </Typography>
          </Box>
          {isExistingJob && initialData && (
            <Chip
              variant="soft"
              color={initialData.published ? 'success' : 'neutral'}
              size="lg"
            >
              {initialData.published ? 'Published' : 'Draft'}
            </Chip>
          )}
        </Stack>
      </Box>

      {renderProgress()}

      {activeStep === 0 && renderStepOne()}
      {activeStep === 1 && renderStepTwo()}
      {activeStep === 2 && renderStepThree()}
      {activeStep === 3 && !isAtsMode && renderStepFour()}

      <Sheet
        variant="plain"
        sx={{
          mt: 4,
          pt: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Button
          variant="plain"
          color="neutral"
          disabled={activeStep === 0}
          onClick={() => {
            setActiveStep(step => Math.max(step - 1, 0));
            scrollToTop();
          }}
          startDecorator={<ArrowBackRoundedIcon />}
        >
          Back
        </Button>

        <Button
          size="lg"
          variant="solid"
          color="primary"
          loading={posting}
          onClick={handleNext}
          endDecorator={activeStep < steps.length - 1 ? <ArrowForwardRoundedIcon /> : <CheckRoundedIcon />}
          sx={{ minWidth: 156, fontWeight: 600 }}
        >
          {activeStep < steps.length - 1 ? 'Continue' : isExistingJob ? 'Save and Continue' : 'Create Job'}
        </Button>
      </Sheet>
    </Box>
  );
};

export default OnboardingJobEditor;
